import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import { PureComponent } from 'react';

import { isNonNullable, isNullable } from '@ipfabric/utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import handleEvent from 'utils/handleEvent';
import OverviewPropTypes from '../../../../OverviewPropTypes';
import ReportItem from './ReportItem';

export default class Leaf extends PureComponent {
  static propTypes = {
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    foundResults: PropTypes.object,
    level: PropTypes.number.isRequired,
    onExpand: PropTypes.func,
    onSelectReport: PropTypes.func.isRequired,
    onSelectReportAddToGroup: PropTypes.func,
    preselectReport: PropTypes.string,
    query: PropTypes.string,
    reportsGroups: OverviewPropTypes.reportsGroups,
    reportsList: PropTypes.object.isRequired,
    selectedGroup: OverviewPropTypes.selectedGroup,
    selectedReport: OverviewPropTypes.selectedReport,
    reportsInGroup: OverviewPropTypes.reportsInGroup,
  };

  constructor(props) {
    super(props);

    const { query, data, foundResults, preselectReport } = props;
    this.state = {
      expanded: isNonNullable(preselectReport)
        ? this.findInReports(query, preselectReport, data, foundResults)
        : false,
    };
  }

  componentDidUpdate(prevProps) {
    const { query, data, foundResults, preselectReport } = this.props;
    if (prevProps.query !== query) {
      if (!query) {
        return this.setState({ expanded: false });
      }
      if (!Array.isArray(data)) {
        this.setState({ expanded: false });
      }
      this.setState({
        expanded: this.findInReports(
          query,
          preselectReport,
          data,
          foundResults,
        ),
      });
    }
    return null;
  }

  getExpandIcon = (itemChildren) => {
    const { expanded } = this.state;
    if (!itemChildren) {
      return <span className='dashboard__empty-icon' />;
    }
    if (expanded) {
      return (
        <i className='fa fa-caret-down dashboard-color-form__caret-icon' />
      );
    }
    return <i className='fa fa-caret-right dashboard-color-form__caret-icon' />;
  };

  getKey = (item, index) => {
    if (isPlainObject(item) && item._key) {
      return item._key;
    }
    if (item.urlList && item.urlList.length === 1) {
      return item.urlList[0].url || index;
    }
    return index;
  };

  getReports = (item) => {
    const {
      reportsList,
      selectedReport,
      level,
      preselectReport,
      query,
      foundResults,
      selectedGroup,
      reportsGroups,
      reportsInGroup,
    } = this.props;
    if (isPlainObject(item) && (item.urlList || item.url)) {
      const url = get(item, ['urlList', 0]) || get(item, ['url']);
      if (!url) {
        return null;
      }
      const list = reportsList[url];
      if (!list) {
        return null;
      }
      return (
        <ul className='list-unstyled pb-0 pl-0'>
          {list.map((l) => (
            <ReportItem
              reportsInGroup={reportsInGroup}
              foundResults={foundResults}
              key={l.id}
              level={level}
              onClick={this.handleSelectReportClick}
              onDoubleClick={this.handleSelectReportDoubleClick}
              preselectReport={preselectReport}
              query={query}
              report={l}
              reportsGroups={reportsGroups}
              selected={selectedReport === l.id}
              selectedGroup={selectedGroup}
            />
          ))}
        </ul>
      );
    }
    return null;
  };

  getChildrenItems = (item) => {
    if (item.children) {
      return item.children;
    }
    if (item.urlList) {
      const filtered = item.urlList.filter((url) => isPlainObject(url));
      if (filtered.length) {
        return filtered;
      }
    }
    return null;
  };

  getName = (data) => {
    if (data.label) {
      return data.label;
    }
    if (data.msg) {
      return (
        get(data, ['msg', 'boxTitle']) ||
        get(data, ['msg', 'title']) ||
        get(data, ['msg', 'pageTitle']) ||
        ''
      );
    }
    return '';
  };

  findInReports = (query, preselectReport, data, foundResults) => {
    if (data.urlList) {
      const found = data.urlList.reduce((acc, url) => {
        if (typeof url === 'string') {
          if (foundResults[url]) {
            return true;
          }
        }
        if (isPlainObject(url)) {
          if (foundResults[url.url]) {
            return true;
          }
        }
        return acc;
      }, false);
      if (found) {
        return true;
      }
    }
    if (data.children) {
      return data.children.reduce((acc, child) => {
        const found = this.findInReports(
          query,
          preselectReport,
          child,
          foundResults,
        );
        if (found) {
          return true;
        }
        return acc;
      }, false);
    }
    return false;
  };

  canSelectReport = (selectedReport) => {
    const { selectedGroup, reportsGroups } = this.props;
    if (isNullable(selectedGroup)) {
      return true;
    }
    if (isNullable(selectedReport)) {
      return false;
    }
    const foundGroup = reportsGroups.find((g) => g.id === selectedGroup);
    if (foundGroup) {
      const foundReport = foundGroup.children.find(
        (r) => r.id === selectedReport,
      );
      if (foundReport) {
        return false;
      }
    }
    return true;
  };

  handleExpand = handleEvent(() => {
    const { expanded } = this.state;
    this.setState({ expanded: !expanded });
  });

  handleExpandTree = handleEvent(() => {
    const { onExpand } = this.props;
    this.setState({ expanded: true });
    if (onExpand) {
      onExpand();
    }
  });

  handleSelectReportClick = handleEvent((e) => {
    const { onSelectReport } = this.props;
    const selectedReport = e.target.getAttribute('data-reportid');
    if (this.canSelectReport(selectedReport)) {
      onSelectReport(selectedReport);
    }
  });

  handleSelectReportDoubleClick = (e) => {
    e.preventDefault();
    const { onSelectReportAddToGroup } = this.props;
    const selectedReport = e.target.getAttribute('data-reportid');
    if (this.canSelectReport(selectedReport) && onSelectReportAddToGroup) {
      onSelectReportAddToGroup(selectedReport);
    }
  };

  render() {
    const {
      data,
      foundResults,
      level,
      onExpand,
      onSelectReport,
      onSelectReportAddToGroup,
      preselectReport,
      query,
      reportsGroups,
      reportsList,
      selectedGroup,
      selectedReport,
      reportsInGroup,
    } = this.props;
    const { expanded } = this.state;
    if (Array.isArray(data)) {
      return (
        <ul className='list-unstyled pb-0 pl-0'>
          {data.map((item, index) => {
            return (
              <Leaf
                data={item}
                foundResults={foundResults}
                key={this.getKey(item, index)}
                level={level}
                onExpand={onExpand}
                onSelectReport={onSelectReport}
                onSelectReportAddToGroup={onSelectReportAddToGroup}
                preselectReport={preselectReport}
                query={query}
                reportsGroups={reportsGroups}
                reportsList={reportsList}
                selectedGroup={selectedGroup}
                selectedReport={selectedReport}
                reportsInGroup={reportsInGroup}
              />
            );
          })}
        </ul>
      );
    }
    if (isPlainObject(data)) {
      const itemChildren = this.getChildrenItems(data);
      return (
        <li>
          <div className='dashboard-color-form__tree-view-item-wrapper'>
            {Array.apply(0, new Array(level))
              .map((_v, i) => i)
              .map((v) => (
                <div key={v} className='dashboard-color-form__empty-space' />
              ))}
            <div style={{ flex: '1' }}>
              <div
                className={classNames(
                  itemChildren ? 'cursor-pointer' : 'cursor-normal',
                )}
                onClick={itemChildren ? this.handleExpand : null}
              >
                {this.getExpandIcon(itemChildren)}
                {this.getName(data)}
              </div>
            </div>
          </div>
          {!itemChildren && this.getReports(data)}
          {itemChildren && expanded ? (
            <Leaf
              data={itemChildren}
              foundResults={foundResults}
              level={level + 1}
              onExpand={this.handleExpandTree}
              onSelectReport={onSelectReport}
              onSelectReportAddToGroup={onSelectReportAddToGroup}
              preselectReport={preselectReport}
              query={query}
              reportsGroups={reportsGroups}
              reportsList={reportsList}
              selectedGroup={selectedGroup}
              selectedReport={selectedReport}
              reportsInGroup={reportsInGroup}
            />
          ) : null}
        </li>
      );
    }
    return null;
  }
}
